import Container from '@atoms/Container';
import ArticleCard from '@sections/ArticleCard';
import apCaseOnlyTitleTags from '@utils/apCaseOnlyTitleTags';
import paddingDefault from '@utils/paddingDefault';
import RichTextEditorStyles from '@utils/RichTextEditorStyles';
import { graphql } from 'gatsby';
import React from 'react';

const Blogroll = ({ data }) => {
    const { heading, subheading, related_content } = data;

    if (!related_content || related_content.length === 0) {
        return null;
    }

    return (
        <section css={paddingDefault()}>
            <Container>
                {related_content && related_content.length > 0 && (
                    <h2 css={styles.blogrollHeading}>
                        {heading ? apCaseOnlyTitleTags(heading) : 'Recommended for You'}
                    </h2>
                )}
                <div
                    css={[RichTextEditorStyles, styles.blogrollSubheading]}
                    dangerouslySetInnerHTML={{ __html: subheading }}
                />
                <ul css={styles.blogrollGrid}>
                    {!!related_content
                        && related_content.map((item) => {
                            // Check if there's raw content at the top
                            const { content, sections, seo, title, url } = item ?? {};
                            const { meta_title, meta_description, meta_image } = seo ?? {};

                            const articleFromHero = () => {
                                const { hero } = sections?.length ? sections[0] : {};

                                // eslint-disable-next-line no-nested-ternary
                                const cardTitle = hero?.heading
                                    ? hero.heading
                                    : meta_title || title;

                                // eslint-disable-next-line no-nested-ternary
                                const cardImage = hero?.image
                                    ? hero.image
                                    : meta_image || '';

                                // eslint-disable-next-line no-nested-ternary
                                const cardContent = hero?.copy
                                    ? hero.copy
                                    : meta_description || '';

                                return {
                                    title: cardTitle,
                                    featured_image: cardImage,
                                    content: cardContent,
                                    url,
                                };
                            };

                            return content ? (
                                <li css={styles.blogrollGridItem} key={item.title}>
                                    <ArticleCard article={item} />
                                </li>
                            ) : (
                                <li css={styles.blogrollGridItem} key={item.title}>
                                    <ArticleCard article={articleFromHero()} />
                                </li>
                            );
                        })}
                </ul>
            </Container>
        </section>
    );
};

const styles = {
    blogrollHeading: {
        fontSize: "32px",
        marginBottom: "1rem",
        lineHeight: 1.25,
        textAlign: "center",
        "@media (min-width: 1024px)": {
            marginBottom: "1.5rem",
        }
    },
    blogrollSubheading: {
        fontSize: "1.125rem",
        lineHeight: "1.75rem",
        textAlign: "center",
        whiteSpace: "pre-line",
        marginRight: "auto",
        marginLeft: "auto",
        marginBottom: "1rem",
        "@media (min-width: 1024px)": {
            marginBottom: "2rem",
            maxWidth: "48rem",
            fontSize: "1.25rem",
            lineHeight: "1.75rem",
        }
    },
    blogrollGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(1, minmax(0, 1fr))",
        gap: "2rem",
        "@media (min-width: 768px)": {
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            rowGap: "4rem",
        },
        "@media (min-width: 1024px)": {
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        }
    },
    blogrollGridItem: {
        flex: "1 1 auto",
        width: "100%",
    }
};

export const query = graphql`
  fragment BlogrollData on CmsPageBuilderBlocks {
    blogroll {
      heading
      subheading
      related_content {
        ...BlogrollArticleFrag
        ...BlogrollPressFrag
        ...BlogrollServiceFrag
        ...BlogrollPageFrag
      }
    }
  }
  fragment BlogrollMetroData on Cms_metro_page_sections {
    blogroll {
      heading
      subheading
      related_content {
        ...BlogrollArticleFrag
        ...BlogrollPressFrag
        ...BlogrollServiceFrag
        ...BlogrollPageFrag
      }
    }
  }
  fragment BlogrollArticleFrag on Cms_article {
    title
    content
    url
    featured_image {
      filename
      title
      description
      url
        contentStackAsset(width: 1440)
    }
  }

  fragment BlogrollPressFrag on Cms_press_release {
    title
    content
    url
    featured_image {
      filename
      title
      description
      url
        contentStackAsset(width: 1440)
    }
  }

  fragment BlogrollServiceFrag on Cms_service_detail {
    title
    url
    sections {
      hero {
        heading
        copy
        image {
          filename
          title
          description
          url
          contentStackAsset(width: 1440)
        }
      }
    }
    seo {
      meta_title
      meta_description
      meta_image {
        filename
        title
        url
        contentStackAsset(width: 1440)
      }
    }
  }

  fragment BlogrollPageFrag on Cms_page_builder {
    title
    url
    sections {
      hero {
        heading
        copy
        image {
          filename
          title
          url
          contentStackAsset(width: 1440)
        }
      }
    }
    seo {
      meta_title
      meta_description
      meta_image {
        filename
        title
        url
        contentStackAsset(width: 1440)
      }
    }
  }
`;

export default Blogroll;
